import React, { useState } from 'react';
import {
    FormGroup,
    ModalBody,
    ModalHeader,
    Input,
    FormText,
} from 'reactstrap';
import './EditEngagementValuesModal.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { DropdownList } from 'react-widgets/cjs';
import { ArrowDown } from '../../../../components/arrow-down/arrow-down';
import { CurrencyInput, Form } from '@availity/form';
import { PercentageField } from '../../../../components/FormFields/PercentageField/PercentageField';
import {
    SfasTransactionFeeValues,
    SfasTransactionFeeErrors,
    NO_SFAS_FEE_ERRORS,
} from '../../../../slices/marketplace/timeline/models/sfas-transaction-fee.model';

interface EditEngagementValuesModalProps {
    engagementId: number;
    currentFteDvm: number;
    currentLtmRevenue: number;
    isVisible: boolean;
    setIsVisible: (isVisible: boolean) => void;
    onSave: (
        fteDvm: number,
        ltmRevenue: number,
        reason: string,
        monthlyEngagementFee?: number,
        ltmRevenueThresholdPercent?: number,
        feePercentOnTotalFairValue?: number,
        feePercentOnExcessFairValue?: number
    ) => void;
    isSaving: boolean;
    isSfasEngagement?: boolean;
    currentMonthlyEngagementFee?: number;
    currentLtmRevenueThresholdPercent?: number;
    currentFeePercentOnTotalFairValue?: number;
    currentFeePercentOnExcessFairValue?: number;
}

const SFAS_FEE_FIELD_LABELS: Record<keyof SfasTransactionFeeValues, string> = {
    ltmRevenueThresholdPercent: '% of LTM Revenue Threshold:',
    feePercentOnTotalFairValue: 'Fee % on Total Fair Value:',
    feePercentOnExcessFairValue: 'Fee % on Excess Fair Value:',
};

const EditEngagementValuesModal = (props: EditEngagementValuesModalProps) => {
    const [fteDvm, setFteDvm] = useState(props.currentFteDvm);
    const [ltmRevenue, setLtmRevenue] = useState(props.currentLtmRevenue);
    const [reason, setReason] = useState('');
    const [monthlyEngagementFee, setMonthlyEngagementFee] = useState(props.currentMonthlyEngagementFee);
    const [isFteDvmError, setIsFteDvmError] = useState(false);
    const [isLtmRevenueError, setIsLtmRevenueError] = useState(false);
    const [isReasonError, setIsReasonError] = useState(false);
    const [isMonthlyEngagementFeeError, setIsMonthlyEngagementFeeError] = useState(false);
    const [sfasFeeValues, setSfasFeeValues] = useState<SfasTransactionFeeValues>({
        ltmRevenueThresholdPercent: props.currentLtmRevenueThresholdPercent,
        feePercentOnTotalFairValue: props.currentFeePercentOnTotalFairValue,
        feePercentOnExcessFairValue: props.currentFeePercentOnExcessFairValue,
    });
    const [sfasFeeErrors, setSfasFeeErrors] = useState<SfasTransactionFeeErrors>(NO_SFAS_FEE_ERRORS);
    const [sfasFeeInputs, setSfasFeeInputs] = useState<Record<keyof SfasTransactionFeeValues, string>>({
        ltmRevenueThresholdPercent: props.currentLtmRevenueThresholdPercent?.toString() ?? '',
        feePercentOnTotalFairValue: props.currentFeePercentOnTotalFairValue?.toString() ?? '',
        feePercentOnExcessFairValue: props.currentFeePercentOnExcessFairValue?.toString() ?? '',
    });

    const currentValues: SfasTransactionFeeValues = {
        ltmRevenueThresholdPercent: props.currentLtmRevenueThresholdPercent,
        feePercentOnTotalFairValue: props.currentFeePercentOnTotalFairValue,
        feePercentOnExcessFairValue: props.currentFeePercentOnExcessFairValue,
    };

    const sfasFeeFields = Object.keys(currentValues) as Array<keyof SfasTransactionFeeValues>;

    const isSfasFeeFieldInError = (field: keyof SfasTransactionFeeValues) =>
        currentValues[field] != null && !(sfasFeeValues[field] > 0);

    const hasSfasFeeError = !!(
        props.isSfasEngagement && sfasFeeFields.some(isSfasFeeFieldInError)
    );

    const getFteDvmsNumberList = () => {
        const fteDvms = [];

        for (let i = 1; i < 50.5; i += 0.5) {
            fteDvms.push({ id: i, value: i.toString() });
        }

        return fteDvms;
    };

    const handleSave = () => {
        let hasErrors = false;

        if (!fteDvm || fteDvm <= 0) {
            setIsFteDvmError(true);
            hasErrors = true;
        } else {
            setIsFteDvmError(false);
        }

        if (!ltmRevenue || ltmRevenue <= 0) {
            setIsLtmRevenueError(true);
            hasErrors = true;
        } else {
            setIsLtmRevenueError(false);
        }

        if (!reason || reason.trim() === '') {
            setIsReasonError(true);
            hasErrors = true;
        } else {
            setIsReasonError(false);
        }

        if (props.isSfasEngagement) {
            const hasCurrentMonthlyEngagementFee = props.currentMonthlyEngagementFee != null;

            if (hasCurrentMonthlyEngagementFee && !(monthlyEngagementFee > 0)) {
                setIsMonthlyEngagementFeeError(true);
                hasErrors = true;
            } else {
                setIsMonthlyEngagementFeeError(false);
            }

            const feeErrors = { ...NO_SFAS_FEE_ERRORS };
            sfasFeeFields.forEach((field) => {
                const isFieldInError = isSfasFeeFieldInError(field);
                feeErrors[field] = isFieldInError;
                if (isFieldInError) {
                    hasErrors = true;
                }
            });
            setSfasFeeErrors(feeErrors);
        }

        if (hasErrors) {
            return;
        }

        const feeToSave = props.isSfasEngagement && monthlyEngagementFee > 0
            ? monthlyEngagementFee
            : undefined;

        const getPercentToSave = (field: keyof SfasTransactionFeeValues) => {
            const value = sfasFeeValues[field];
            return props.isSfasEngagement && value != null && value > 0
                ? value
                : undefined;
        };

        props.onSave(
            fteDvm,
            ltmRevenue,
            reason.trim(),
            feeToSave,
            getPercentToSave('ltmRevenueThresholdPercent'),
            getPercentToSave('feePercentOnTotalFairValue'),
            getPercentToSave('feePercentOnExcessFairValue')
        );
    };

    const renderPercentField = (
        label: string,
        field: keyof SfasTransactionFeeValues
    ) => {
        const isError = sfasFeeErrors[field];

        return (
            <FormGroup className="fee-percent-form-group">
                <div className="form-field-container">
                    <div className="mobile-label">
                        <label className="copy-name-label">{label}</label>
                    </div>
                    <div className="mobile-input">
                        <Form
                            onSubmit={() => { }}
                            initialValues={{
                                [field]: sfasFeeInputs[field],
                            }}>
                            <PercentageField
                                formKey={field}
                                id={field}
                                includeDecimals={true}
                                decimalPoints={2}
                                value={sfasFeeInputs[field]}
                                className={`${isError ? 'av-invalid is-invalid' : ''} fee-percent-input`}
                                updateValue={(newValue) => {
                                    const stripped = newValue.replace('%', '');
                                    setSfasFeeInputs((prev) => ({
                                        ...prev,
                                        [field]: stripped,
                                    }));
                                    if (stripped === '') {
                                        setSfasFeeValues((prev) => ({
                                            ...prev,
                                            [field]: undefined,
                                        }));
                                    } else {
                                        const numValue = parseFloat(stripped);
                                        if (!isNaN(numValue)) {
                                            setSfasFeeValues((prev) => ({
                                                ...prev,
                                                [field]: numValue,
                                            }));
                                            if (numValue > 0) {
                                                setSfasFeeErrors((prev) => ({
                                                    ...prev,
                                                    [field]: false,
                                                }));
                                            }
                                        }
                                    }
                                }}
                            />
                        </Form>
                    </div>
                </div>
            </FormGroup>
        );
    };

    return (
        <Rodal
            visible={props.isVisible}
            onClose={() => {
                props.setIsVisible(false);
            }}
            animation={'fade'}
            showMask={false}
            width={750}
            className="edit-engagement-values-modal-rodal"
            center>
            <div className="edit-engagement-values-modal">
                <ModalHeader>Edit Engagement Values</ModalHeader>
                <ModalBody>
                    <FormGroup className="fte-dvm-form-group">
                        <div className="form-field-container">
                            <div className="mobile-label">
                                <label className="copy-name-label">FTE DVMs, at engagement start:</label>
                            </div>
                            <div className="mobile-input">
                                <DropdownList
                                    selectIcon={<ArrowDown />}
                                    className={`dropbox-field div-field-value ${isFteDvmError ? 'av-invalid is-invalid' : ''
                                        }`}
                                    data={getFteDvmsNumberList()}
                                    value={fteDvm}
                                    onChange={(value: any) => {
                                        setFteDvm(value.id);
                                        if (value.id > 0) {
                                            setIsFteDvmError(false);
                                        }
                                    }}
                                    dataKey="id"
                                    textField="value"
                                />
                            </div>
                        </div>
                    </FormGroup>

                    <FormGroup className="ltm-revenue-form-group">
                        <div className="form-field-container">
                            <div className="mobile-label">
                                <label className="copy-name-label">
                                    LTM Revenue, at engagement start:
                                </label>
                            </div>
                            <div className="mobile-input">
                                <Form
                                    onSubmit={() => { }}
                                    initialValues={{
                                        ltmRevenue: ltmRevenue != null ? ltmRevenue.toString() : '',
                                    }}>
                                    <CurrencyInput
                                        name="ltmRevenue"
                                        id="ltmRevenue"
                                        value={ltmRevenue != null ? ltmRevenue.toString() : ''}
                                        allowDecimals={true}
                                        allowNegativeValue={false}
                                        prefix="$"
                                        onValueChanged={(value) => {
                                            const numValue = parseFloat(value) || 0;
                                            setLtmRevenue(numValue);
                                            if (numValue > 0) {
                                                setIsLtmRevenueError(false);
                                            }
                                        }}
                                        className={isLtmRevenueError ? 'av-invalid is-invalid' : ''}
                                    />
                                </Form>
                            </div>
                        </div>
                    </FormGroup>

                    {props.isSfasEngagement && (
                        <FormGroup className="monthly-engagement-fee-form-group">
                            <div className="form-field-container">
                                <div className="mobile-label">
                                    <label className="copy-name-label">
                                        Monthly Engagement Fee:
                                    </label>
                                </div>
                                <div className="mobile-input">
                                    <Form
                                        onSubmit={() => { }}
                                        initialValues={{
                                            monthlyEngagementFee: monthlyEngagementFee != null ? monthlyEngagementFee.toString() : '',
                                        }}>
                                        <CurrencyInput
                                            name="monthlyEngagementFee"
                                            id="monthlyEngagementFee"
                                            value={monthlyEngagementFee != null ? monthlyEngagementFee.toString() : ''}
                                            allowDecimals={true}
                                            allowNegativeValue={false}
                                            prefix="$"
                                            onValueChanged={(value) => {
                                                if (value === '' || value == null) {
                                                    setMonthlyEngagementFee(undefined);
                                                    setIsMonthlyEngagementFeeError(false);
                                                    return;
                                                }
                                                const numValue = parseFloat(value);
                                                if (isNaN(numValue)) {
                                                    return;
                                                }
                                                setMonthlyEngagementFee(numValue);
                                                if (numValue > 0) {
                                                    setIsMonthlyEngagementFeeError(false);
                                                }
                                            }}
                                            className={isMonthlyEngagementFeeError ? 'av-invalid is-invalid' : ''}
                                        />
                                    </Form>
                                </div>
                            </div>
                        </FormGroup>
                    )}

                    {props.isSfasEngagement && renderPercentField(
                        SFAS_FEE_FIELD_LABELS.ltmRevenueThresholdPercent,
                        'ltmRevenueThresholdPercent'
                    )}

                    {props.isSfasEngagement && renderPercentField(
                        SFAS_FEE_FIELD_LABELS.feePercentOnTotalFairValue,
                        'feePercentOnTotalFairValue'
                    )}

                    {props.isSfasEngagement && renderPercentField(
                        SFAS_FEE_FIELD_LABELS.feePercentOnExcessFairValue,
                        'feePercentOnExcessFairValue'
                    )}

                    <FormGroup>
                        <label className="copy-name-label reason">Reason:</label>
                        <Input
                            type="textarea"
                            name="reason"
                            id="reason"
                            rows={4}
                            value={reason}
                            onChange={(e: any) => {
                                setReason(e.target.value);
                                if (e.target.value.trim() !== '') {
                                    setIsReasonError(false);
                                }
                            }}
                            className={isReasonError ? 'av-invalid is-invalid' : ''}
                            placeholder="Enter reason for changing engagement values"
                        />
                        {isReasonError && (
                            <FormText color="danger" className="mt-1">
                                Reason is required
                            </FormText>
                        )}
                    </FormGroup>
                    <div className="mt-auto d-flex justify-content-end modal-copy-buttons">
                        <button
                            type="button"
                            className="btn btn-cancel"
                            onClick={() => {
                                props.setIsVisible(false);
                            }}>
                            Cancel
                        </button>
                        <ButtonLoader
                            buttonText={'Save'}
                            disabled={
                                fteDvm <= 0 ||
                                ltmRevenue <= 0 ||
                                !reason ||
                                reason.trim() === '' ||
                                (props.isSfasEngagement &&
                                    props.currentMonthlyEngagementFee != null &&
                                    !(monthlyEngagementFee > 0)) ||
                                hasSfasFeeError
                            }
                            isLoading={props.isSaving}
                            onClick={handleSave}
                            className={props.isSaving ? 'btn btn-primary loader-submit' : 'btn btn-primary'}
                            loaderButtonText={undefined}></ButtonLoader>
                    </div>
                </ModalBody>
            </div>
        </Rodal>
    );
};

export default EditEngagementValuesModal;
